import { Config } from '@/config';
import { ApiService } from '@/services/api.service';

/**
 * Tender-views persistence — talks to the live `aloha.api.tender` service.
 *
 *   GET    /tender-views                 -> [TenderViewDto]  (own private + tenant shared + presets, pre-sorted)
 *   POST   /tender-views                 -> TenderViewDto     body { name, scope, filters, preset? }
 *   PUT    /tender-views/{id}            -> TenderViewDto     body { name?, scope?, filters?, preset? } (patch)
 *   DELETE /tender-views/{id}            -> 200
 *   PUT    /tender-views/{id}/aviseur    -> 200               body { enabled }  (per-user opt-in)
 *   PUT    /tender-views/order           -> 200               body { orderedIds } (this user's personal order)
 *   PUT    /tender-views/presets/order   -> 200               body { orderedIds } (admin only)
 *
 * The server derives userId/tenantId from the JWT and enforces scoping + edit/delete
 * permission; the DTO's `canEdit` is advisory for the UI.
 */

const base = () => `${Config.api.tender.url}tender-views`;

class TenderViewsService {
  list() {
    return ApiService.get(base()).then((response) => response.data);
  }

  create(view) {
    return ApiService.post(base(), view).then((response) => response.data);
  }

  update(id, patch) {
    return ApiService.put(`${base()}/${id}`, patch).then((response) => response.data);
  }

  remove(id) {
    return ApiService.delete(`${base()}/${id}`).then((response) => response.data);
  }

  setAviseur(id, enabled) {
    return ApiService.put(`${base()}/${id}/aviseur`, { enabled }).then((response) => response.data);
  }

  // Persist this user's personal order of ALL their visible views (send the full ordered id list).
  reorder(orderedIds) {
    return ApiService.put(`${base()}/order`, { orderedIds }).then((response) => response.data);
  }

  // Admin-only: persist the tenant-wide order of curated preset views.
  reorderPresets(orderedIds) {
    return ApiService.put(`${base()}/presets/order`, { orderedIds }).then((response) => response.data);
  }

  // Batched tab-badge counts for a set of views (one round trip).
  // Kept available for when per-view tab counts are re-enabled; not currently called.
  getViewCounts(viewIds) {
    return ApiService.post(`${Config.api.tender.url}tenders/search/counts`, { viewIds }).then(
      (response) => response.data,
    );
  }
}

export const tenderViewsService = new TenderViewsService();
